import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, ArrowLeft, Loader2, AlertCircle, Check } from 'lucide-react';
import { step3Schema, type Step3Data } from '@/lib/validations';
import { cn } from '@/lib/utils';

const services = [
  { id: 'paid-media', label: 'Paid Media & Media Buying' },
  { id: 'ott-ctv', label: 'OTT/CTV Streaming TV Advertising' },
  { id: 'programmatic', label: 'Programmatic Advertising' },
  { id: 'social-media', label: 'Social Media Advertising' },
  { id: 'geo-targeted', label: 'Geo-Targeted Campaigns' },
  { id: 'subcontracting', label: 'Subcontracting / Partnership' },
  { id: 'not-sure', label: 'Not sure yet — need guidance' },
];

const objectives = [
  'Build Brand / Program Awareness',
  'Generate Leads / Inquiries',
  'Drive Website Traffic',
  'Increase Sales / Conversions',
  'Recruitment / Hiring',
  'Event Promotion',
  'Public Education / Civic Engagement',
  'Multiple objectives — will discuss',
];

const referralSources = [
  'Google / Search Engine',
  'LinkedIn',
  'Referral from Colleague',
  'Government Contracting Database (SAM.gov, etc.)',
  'Industry Event / Conference',
  'Other',
  'Don\'t remember',
];

interface Step3GoalsProps {
  initialData?: Partial<Step3Data>;
  onSubmit: (data: Step3Data) => Promise<void>;
  onBack: () => void;
  isSubmitting: boolean;
}

export function Step3Goals({ initialData, onSubmit, onBack, isSubmitting }: Step3GoalsProps) {
  const [formData, setFormData] = useState<Step3Data>({
    services_interested: initialData?.services_interested || [],
    primary_objective: initialData?.primary_objective || '',
    referral_source: initialData?.referral_source || '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof Step3Data, string>>>({});

  const toggleService = (serviceId: string) => {
    setFormData(prev => {
      const current = prev.services_interested;
      const updated = current.includes(serviceId)
        ? current.filter(s => s !== serviceId)
        : [...current, serviceId];
      return { ...prev, services_interested: updated };
    });
    if (errors.services_interested) {
      setErrors(prev => ({ ...prev, services_interested: undefined }));
    }
  };

  const handleChange = (name: keyof Step3Data, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = step3Schema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof Step3Data, string>> = {};
      result.error.errors.forEach((error) => {
        const field = error.path[0] as keyof Step3Data;
        fieldErrors[field] = error.message;
      });
      setErrors(fieldErrors);
      return;
    }

    await onSubmit(result.data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <Label className="text-foreground text-sm font-medium block mb-3">
          Services You're Interested In * <span className="text-muted-foreground font-normal">(select all that apply)</span>
        </Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {services.map((service) => {
            const isChecked = formData.services_interested.includes(service.id);
            return (
              <button
                key={service.id}
                type="button"
                onClick={() => toggleService(service.id)}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg border text-left transition-all duration-200',
                  isChecked 
                    ? 'border-gold bg-gold/10 text-foreground' 
                    : 'border-border bg-card hover:border-gold/50'
                )}
              >
                <div className={cn(
                  'w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors',
                  isChecked ? 'bg-gold border-gold' : 'border-border'
                )}>
                  {isChecked && <Check className="w-3 h-3 text-dark-bg" />}
                </div>
                <span className="text-sm">{service.label}</span>
              </button>
            );
          })}
        </div>
        {errors.services_interested && (
          <p className="text-destructive text-xs mt-2 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.services_interested}
          </p>
        )}
      </div>

      <div>
        <Label className="text-foreground text-sm font-medium">
          Primary Campaign Objective *
        </Label>
        <Select
          value={formData.primary_objective}
          onValueChange={(value) => handleChange('primary_objective', value)}
        >
          <SelectTrigger className={`mt-1.5 ${errors.primary_objective ? 'border-destructive' : ''}`}>
            <SelectValue placeholder="Select one..." />
          </SelectTrigger>
          <SelectContent>
            {objectives.map((obj) => (
              <SelectItem key={obj} value={obj}>{obj}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.primary_objective && (
          <p className="text-destructive text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.primary_objective}
          </p>
        )}
      </div>

      <div>
        <Label className="text-foreground text-sm font-medium">
          How Did You Hear About Us? *
        </Label>
        <Select
          value={formData.referral_source}
          onValueChange={(value) => handleChange('referral_source', value)}
        >
          <SelectTrigger className={`mt-1.5 ${errors.referral_source ? 'border-destructive' : ''}`}>
            <SelectValue placeholder="Select one..." />
          </SelectTrigger>
          <SelectContent>
            {referralSources.map((source) => (
              <SelectItem key={source} value={source}>{source}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.referral_source && (
          <p className="text-destructive text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.referral_source}
          </p>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="flex-1"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 bg-gold hover:bg-gold-light text-dark-bg font-semibold"
        >
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
